/**
 * Pure derivations for the tenant-detail "Übersicht" dashboard (Slice O1a).
 *
 * No fetching, no React, no clock reading beyond what a caller passes in — so
 * every rule below is unit-testable without rendering. All of them read data
 * the page ALREADY loads on mount; nothing here invents a value the backend
 * does not state.
 */

/**
 * The tenancy the tenant is living in today.
 *
 * `is_running` is served per row by the backend
 * (`tenancy_admin_service.tenancy_to_dict` → `tenancy_is_running_on`), resolved
 * against the SWISS business day. It is deliberately used instead of
 * `status === "active"`: production carries many payment-liable tenancies whose
 * raw status is still `reserved` while `move_in_date` is already past
 * (`revenue_forecast.payment_liable_for_month_clauses` documents exactly this),
 * and a raw-status filter would drop them.
 *
 * A tenant CAN have two running tenancies at once — the DB exclusion constraint
 * is scoped to `room_id`, not `tenant_id`, so an internal move overlaps by
 * design. The later `move_in_date` wins (that is the one they moved INTO);
 * `created_at` breaks a same-day tie deterministically.
 */
export function selectCurrentTenancy(tenancies) {
  const list = Array.isArray(tenancies) ? tenancies : [];
  const running = list.filter((t) => t && t.is_running === true);
  if (running.length === 0) return null;
  const sorted = [...running].sort((a, b) => {
    const ma = String(a.move_in_date || "");
    const mb = String(b.move_in_date || "");
    if (ma !== mb) return ma < mb ? 1 : -1;
    const ca = String(a.created_at || "");
    const cb = String(b.created_at || "");
    if (ca !== cb) return ca < cb ? 1 : -1;
    return 0;
  });
  return sorted[0];
}

/**
 * The tenant's current address row, from the address book contract
 * (`GET /api/admin/tenants/{id}/addresses`). The backend already excludes
 * soft-deleted rows and a partial unique index guarantees at most one
 * `is_current` row per tenant.
 *
 * Returns null when the tenant has no address rows yet — the caller decides
 * what to say, and must NOT silently substitute the flat legacy
 * `tenant.street/...` fields, which are a mirror rather than the record.
 */
export function selectCurrentAddress(addresses) {
  const list = Array.isArray(addresses) ? addresses : [];
  return list.find((a) => a && a.is_current === true) || null;
}

/** `["Strasse", "PLZ Ort", "Land"]`, empty segments dropped. */
export function addressLines(address) {
  if (!address) return [];
  const street = String(address.street || "").trim();
  const plz = String(address.postal_code || "").trim();
  const city = String(address.city || "").trim();
  const country = String(address.country || "").trim();
  const line2 = [plz, city].filter(Boolean).join(" ");
  return [street, line2, country].filter(Boolean);
}

/* ── Deposit ─────────────────────────────────────────────────────────────── */

const KNOWN_DEPOSIT_TYPES = new Set(["cash", "insurance", "bank", "none"]);

/**
 * `(effective, source)` for one tenancy's deposit sum.
 *
 * COALESCE, never a sum — mirrors `dashboard_deposit_service._tenant_amounts`.
 * `deposit_amount` predates the `tenant_deposit_*` block (migration 034, no
 * backfill), so a row may legitimately carry either; adding them would invent a
 * security that was never placed. Presence, not truthiness: a canonical 0 is a
 * recorded zero and wins over a legacy figure.
 */
function depositAmount(tenancy) {
  const canonical = tenancy?.tenant_deposit_amount;
  const legacy = tenancy?.deposit_amount;
  if (canonical !== null && canonical !== undefined) {
    return { amount: Number(canonical), source: "canonical" };
  }
  if (legacy !== null && legacy !== undefined) {
    return { amount: Number(legacy), source: "legacy" };
  }
  return { amount: 0, source: null };
}

/**
 * Shape one tenancy's deposit for display. NO CLASSIFICATION HAPPENS HERE.
 *
 * This used to re-implement `derive_tenant_security_state` in JavaScript, and
 * the copy went stale the moment the backend rule changed: after K4x.1 an
 * unconfirmed guarantee was `pending` on /admin/kautionen and still "Aktiv"
 * here — one tenancy, two answers, and no way for a reader to tell which was
 * current. The state now arrives on the wire (`tenancy_admin_service`
 * serializes it from the same helper the aggregate uses) and is passed through
 * untouched.
 *
 * There is deliberately no local fallback for a missing `security_state`: a
 * fallback is the duplicate rule again, only harder to find. A response without
 * the field yields `null` and the card renders its empty state.
 */
export function selectDepositView(tenancy) {
  if (!tenancy) return null;
  const type = String(tenancy.tenant_deposit_type || "").trim().toLowerCase();
  const { amount, source } = depositAmount(tenancy);

  return {
    state: tenancy.security_state ?? null,
    amount,
    source,
    type,
    knownType: KNOWN_DEPOSIT_TYPES.has(type),
    provider: tenancy.tenant_deposit_provider || null,
    receivedAt: tenancy.deposit_received_at || null,
    returnedAt: tenancy.deposit_returned_at || null,
  };
}

/* ── Invoices ────────────────────────────────────────────────────────────── */

/**
 * What the Finanzen card may claim about this tenant's VANTIO invoices.
 *
 * THE NATIVE WORLD'S SELECTOR, AND ONLY THAT (TINV-2). It reads the newest 20
 * rows of `GET /api/invoices?tenant_id=` (ordered `issue_date desc`), whose
 * `status` is the backend's `effective_status`, already resolved against the
 * Swiss business day — so an overdue row in the window is a fact, not a
 * client-side guess.
 *
 * But the window is the wrong end for overdue rows: they are by definition old.
 * So a COUNT is only stated when the window provably holds every invoice
 * (`total <= items.length`). Otherwise `complete` is false and the caller must
 * say nothing about the payment state — an unproven "alles bezahlt" is the worst
 * possible error on a card meant to create trust in two seconds.
 *
 * IT IS NOT REACHED AT ALL FOR A BEXIO-BILLING ORGANIZATION, and that is now the
 * caller's job rather than an omission here. This file used to record that the
 * Bexio mirror was "deliberately not consulted" because `sync_invoices` writes
 * no tenant mapping FK; TINV-1 resolves the tenant question through the draft
 * ledger as well as that column, so the mirror can be asked properly — by
 * `useTenantInvoiceOverview`, against its own endpoint, with no shared shape.
 * Nothing in this function understands a Bexio invoice, and it must never be
 * handed one: `bexio_status` is the provider's word, not an `effective_status`,
 * and counting it here would manufacture the canonical payment state the Bexio
 * wire deliberately does not carry.
 */
export function selectInvoiceOverview(invoices, total) {
  const list = Array.isArray(invoices) ? invoices : [];
  const knownTotal = Number.isFinite(Number(total)) ? Number(total) : null;
  const complete = knownTotal !== null && knownTotal <= list.length;
  const overdueCount = list.filter(
    (inv) => String(inv?.status || "").toLowerCase() === "overdue"
  ).length;
  const unpaidCount = list.filter(
    (inv) => String(inv?.status || "").toLowerCase() === "unpaid"
  ).length;
  return {
    complete,
    total: knownTotal,
    loadedCount: list.length,
    overdueCount: complete ? overdueCount : null,
    unpaidCount: complete ? unpaidCount : null,
  };
}

/* ── Move-in documents ───────────────────────────────────────────────────── */

/**
 * The move-in handover protocol for the given tenancy, from the inspections the
 * page already loads (`useTenantInspections`). `signed_at` is the tenant's
 * signature; `finalized_at` is only the edit lock, so it is NOT read as "signed".
 *
 * Only the move-in type counts — a move-out protocol says nothing about the
 * move-in. Newest signed row wins when several exist.
 *
 * The rental contract and the key receipt have the same shape of evidence but
 * are fetched per tenancy inside `RentalContractSection` / `HandoverSection`,
 * not at page level. Lifting them would add requests this slice is not allowed
 * to add, so O1a states only what the page can already prove.
 */
export function selectMoveInProtocol(inspections, tenancyId) {
  if (!tenancyId) return null;
  const list = Array.isArray(inspections) ? inspections : [];
  const relevant = list.filter(
    (i) =>
      i &&
      String(i.tenancy_id) === String(tenancyId) &&
      String(i.type || "").toLowerCase() === "move_in"
  );
  if (relevant.length === 0) return null;
  const signed = relevant
    .filter((i) => i.signed_at)
    .sort((a, b) => (String(a.signed_at) < String(b.signed_at) ? 1 : -1));
  if (signed.length > 0) {
    return { exists: true, signedAt: signed[0].signed_at, id: signed[0].id };
  }
  return { exists: true, signedAt: null, id: relevant[0].id };
}

/* ── Activity ────────────────────────────────────────────────────────────── */

/**
 * The most recent entry across the two streams the Verlauf actually reads
 * (`tenant_events` + `audit_logs` scoped to `entity_type=tenant`). The page
 * already merges and sorts them into `mergedHistoryEvents`, so this only reads
 * the head — no second sort, no second source of truth.
 */
export function selectLastActivityAt(mergedHistoryEvents) {
  const list = Array.isArray(mergedHistoryEvents) ? mergedHistoryEvents : [];
  const first = list[0];
  return first?.created_at || null;
}

/* ── Tasks ───────────────────────────────────────────────────────────────── */

/** Statuses that mean "still to be done". `done` and `cancelled` are finished. */
export const OPEN_TASK_STATUSES = ["todo", "in_progress", "blocked"];

/**
 * The few open tasks the card shows, plus the true total.
 *
 * `total` comes from the list response, not `items.length` — a card that says
 * "5" when the server holds 40 is a lie the reader cannot detect.
 */
export function selectOpenTasks(taskResponse, limit = 4) {
  const items = Array.isArray(taskResponse?.items) ? taskResponse.items : [];
  const rawTotal = taskResponse?.total;
  const total = Number.isFinite(Number(rawTotal))
    ? Number(rawTotal)
    : items.length;
  return { items: items.slice(0, limit), total, shown: Math.min(items.length, limit) };
}
